import { Calendar, defaultCalendarStrings } from '@fluentui/react-calendar-compat';
import { Input, Option, Popover, PopoverSurface, PopoverTrigger, useId } from '@fluentui/react-components';
import { CalendarMonthRegular } from '@fluentui/react-icons';
import { TimePicker, TimeSelectionData, TimeSelectionEvents } from '@fluentui/react-timepicker-compat';
import { format } from 'date-fns';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { SelectOptions } from '../form';
import './PopoverDateTimePicker.scss';

interface PopoverDateTimePickerProps {
  id: string;
  dateTime?: Date;
  disabled?: boolean;
  dateLabel?: string;
  timeLabel?: string;
  datePlaceholder?: string;
  onDateTimeChange: (dateTime: Date | null) => void;
}

const PopoverDateTimePickerImpl: React.FC<PopoverDateTimePickerProps> = (props) => {
  const {
    id,
    dateTime,
    disabled,
    dateLabel = 'Date',
    timeLabel = 'Time',
    datePlaceholder = 'Select a date',
    onDateTimeChange,
  } = props;

  const dialogId = useId('Popover-date-time-picker-dialog');
  const [anchorEl, setAnchorEl] = useState<HTMLInputElement | null>(null);

  const initialTime = dateTime ?? new Date();

  const [date, setDate] = useState<Date | null>(dateTime ?? null);
  const [time, setTime] = useState<Date>(initialTime);
  const [timeText, setTimeText] = useState<string>(format(initialTime, 'h:mm'));
  const [selectedAmPm, setSelectedAmPm] = useState(initialTime.getHours() < 12 ? 'AM' : 'PM');
  const [open, setOpen] = useState<boolean>(false);

  const handleOpen = (): void => {
    if (!disabled) setOpen(true);
  };

  const applyDateTime = (nextDate: Date, nextTime: Date): void => {
    const mergedDateTime = new Date(nextDate);
    mergedDateTime.setHours(nextTime.getHours(), nextTime.getMinutes(), 0, 0);
    setDate(mergedDateTime);
    setTime(mergedDateTime);
    setTimeText(format(mergedDateTime, 'h:mm'));
    setSelectedAmPm(mergedDateTime.getHours() < 12 ? 'AM' : 'PM');
    onDateTimeChange(mergedDateTime);
  };

  const handleDateChange = (selectedDate: Date | null | undefined): void => {
    if (!selectedDate) {
      setDate(null);
      onDateTimeChange(null);
      return;
    }

    applyDateTime(selectedDate, time);
    setOpen(false);
  };

  const handleTimeChange = (_event: TimeSelectionEvents, data: TimeSelectionData): void => {
    if (!data.selectedTime || !date) return;

    const selectedTimeText = data.selectedTimeText?.toUpperCase();
    let nextAmPm = selectedAmPm;
    if (selectedTimeText?.includes('AM')) nextAmPm = 'AM';
    if (selectedTimeText?.includes('PM')) nextAmPm = 'PM';

    const adjustedTime = new Date(data.selectedTime);
    const hours = adjustedTime.getHours();

    if (nextAmPm === 'AM' && hours >= 12) {
      adjustedTime.setHours(hours - 12);
    } else if (nextAmPm === 'PM' && hours < 12) {
      adjustedTime.setHours(hours + 12);
    }

    applyDateTime(date, adjustedTime);
  };

  const handleTimeInput = (event: React.FormEvent<HTMLInputElement>): void => {
    setTimeText(event.currentTarget.value);
  };

  const handleSelectedAmPmChange = (optionValue?: string): void => {
    if ((optionValue !== 'AM' && optionValue !== 'PM') || !date) return;

    const adjustedTime = new Date(time);
    const hours = adjustedTime.getHours();

    if (optionValue === 'AM' && hours >= 12) {
      adjustedTime.setHours(hours - 12);
    } else if (optionValue === 'PM' && hours < 12) {
      adjustedTime.setHours(hours + 12);
    }

    applyDateTime(date, adjustedTime);
  };

  useEffect(() => {
    const nextDateTime = dateTime ?? new Date();

    setDate(dateTime ?? null);
    setTime(nextDateTime);
    setTimeText(format(nextDateTime, 'h:mm'));
    setSelectedAmPm(nextDateTime.getHours() < 12 ? 'AM' : 'PM');
  }, [dateTime?.getTime()]);

  const displayText = date
    ? `${format(date, 'MM/dd/yyyy')}, ${timeText ? `${timeText} ${selectedAmPm}` : ''}`
    : '';

  return (
    <Popover
      open={open}
      onOpenChange={(_event, data) => setOpen(disabled ? false : data.open)}
      positioning={{ position: 'below', align: 'start', target: anchorEl }}
    >
      <div className='popover-date-time-picker__trigger'>
        <PopoverTrigger disableButtonEnhancement>
          <Input
            id={id}
            ref={setAnchorEl}
            aria-label={dateLabel}
            role='combobox'
            value={displayText}
            placeholder={datePlaceholder}
            readOnly
            disabled={disabled}
            className='popover-date-time-picker__input'
            onClick={handleOpen}
            aria-controls={dialogId}
            aria-expanded={open}
            contentAfter={
              <span
                className='popover-date-time-picker__icon-button'
                aria-hidden
                onClick={handleOpen}
              >
                <CalendarMonthRegular
                  className='popover-date-time-picker__icon'
                  aria-hidden
                />
              </span>
            }
          />
        </PopoverTrigger>
      </div>
      <PopoverSurface
        id={dialogId}
        role='dialog'
        aria-label={dateLabel}
        className='popover-date-time-picker__dropdown'
      >
        <Calendar
          value={date ?? new Date()}
          onSelectDate={handleDateChange}
          strings={defaultCalendarStrings}
          showGoToToday
        />
        <div className='popover-date-time-picker__time-row'>
          <TimePicker
            freeform
            id={`${id}-time`}
            aria-label={timeLabel}
            selectedTime={time}
            value={timeText}
            increment={15}
            onInput={handleTimeInput}
            onTimeChange={handleTimeChange}
            placeholder='Time'
            inlinePopup
            disabled={disabled}
          />
          <SelectOptions
            className='popover-date-time-picker__am-pm'
            dropdownProps={{
              'aria-label': 'AM or PM selector',
              inlinePopup: true,
              selectedOptions: [selectedAmPm],
              value: selectedAmPm,
              disabled,
            }}
            onOptionSelect={(data) => handleSelectedAmPmChange(data.optionValue)}
          >
            <Option value='AM'>AM</Option>
            <Option value='PM'>PM</Option>
          </SelectOptions>
        </div>
      </PopoverSurface>
    </Popover>
  );
};

export const PopoverDateTimePicker = observer(PopoverDateTimePickerImpl);
